import { message } from "antd";
import dayjs, { Dayjs } from "dayjs";
import React, { useEffect, useRef } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { AppDispatch } from "../../../../redux/store";
import { LeaseTerms } from "../../../../types/bookings";
import { SelectOption } from "../../../../types/select";
import { findSelectOption } from "../../../libs/form";
import { convertToDayjs, formatDateByType } from "../../../libs/utils";
import PopupManager from "../../../organism/LeaseConfiguration/components/PopupManager";
import { usePopupManager } from "../../../organism/LeaseConfiguration/hooks/usePopupManager";
import { CommonCard } from "../components";
import { getBookingDetail, setBookingForm, setFormState, updateBooking } from "../ducks/actions";
import { LeaseEndDateRefType } from "./NumberOfYearsSelect";
import ResidentLeaseTermsForm from "./ResidentLeaseTermsForm";
import {
  calculateRentFree,
  calculateYears,
  dateFormat,
  excludedLeaseTermsKeys,
  filterDropdownValue,
  generateYearOptions,
  parkingPayload,
  rentFreePeriodValues,
} from "./utils";

export type LeaseTermFormData = {
  leaseManager?: string;
  externalAgent?: string;
  leaseNature?: SelectOption;
  numberOfOcupants?: string;
  bookingStartDate?: string | Dayjs;
  bookingEndDate?: string | Dayjs;
  fitOutStartDate?: string | Dayjs;
  fitOutEndDate?: string | Dayjs;
  leaseStartDate?: string | Dayjs;
  leaseEndDate?: string | Dayjs;
  numberOfYears: SelectOption;
  rentFreePeriod?: SelectOption;
  commencementDate?: string | Dayjs;
  moveInDate?: string | Dayjs;
  parkings?: unknown[];
  noOfFreeParkingSlots: number;
};

interface IProps {
  parentState: (obj: object) => void;
}

const index = (props: IProps) => {
  const dispatch = useDispatch<AppDispatch>();

  const { bookingForm, landlordUsers, bookingDetails } = useSelector((state: any) => state.newBooking);

  const leaseEndDateRef = useRef<LeaseEndDateRefType>({ actual: null, withRentFreePeriod: null });

  const { visible, setVisible, setTrigger, trigger } = usePopupManager();

  const { leaseId = bookingDetails.leaseId } = useParams();
  const yearsOptions = generateYearOptions({ start: 1, end: 10, withCustomOption: true });
  const rentFreePeriodOptions = rentFreePeriodValues() as SelectOption[];

  const getInitialBookingDates = () => {
    const { bookingStartDate, bookingEndDate, moveInDate } = bookingForm?.bookingTerms || {};
    const { leaseStartDate, leaseEndDate, commencementDate } = (bookingForm?.leaseTerms || {}) as LeaseTerms;

    const now = dayjs();
    const defaultStart = now.add(14, "day");

    const initialLeaseStartDate = convertToDayjs(leaseStartDate) || defaultStart;
    const initialLeaseEndDate = convertToDayjs(leaseEndDate) || defaultStart.add(1, "year").subtract(1, "day");

    return {
      initialBookingStartDate: convertToDayjs(bookingStartDate) || now,
      initialBookingEndDate: convertToDayjs(bookingEndDate) || defaultStart,
      initialLeaseStartDate,
      initialLeaseEndDate,
      initialRentFreeStartDate: initialLeaseEndDate,
      initialCommencementDate: convertToDayjs(commencementDate) || initialLeaseStartDate,
      initialMoveInDate: convertToDayjs(moveInDate) || defaultStart,
    };
  };

  const {
    initialBookingStartDate,
    initialBookingEndDate,
    initialLeaseStartDate,
    initialLeaseEndDate,
    initialCommencementDate,
    initialMoveInDate,
  } = getInitialBookingDates();

  const methods = useForm<LeaseTermFormData>({
    defaultValues: {
      leaseManager: filterDropdownValue(bookingForm?.leaseManager?.userId || bookingForm?.leaseManager, landlordUsers),
      externalAgent: bookingForm?.externalAgent || "",
      numberOfOcupants: bookingForm?.numberOfOcupants || 1,
      bookingStartDate: initialBookingStartDate,
      bookingEndDate: initialBookingEndDate,
      fitOutStartDate: dateFormat(bookingForm?.leaseTerms?.fitOutStartDate),
      fitOutEndDate: dateFormat(bookingForm?.leaseTerms?.fitOutEndDate),
      leaseStartDate: initialLeaseStartDate,
      leaseEndDate: initialLeaseEndDate,
      numberOfYears:
        findSelectOption(yearsOptions, bookingForm?.leaseTerms?.numberOfYears) ??
        calculateYears(bookingForm?.leaseTerms?.leaseStartDate, bookingForm?.leaseTerms?.leaseEndDate) ??
        yearsOptions[0],
      rentFreePeriod:
        findSelectOption(rentFreePeriodOptions, bookingForm?.leaseTerms?.rentFreePeriod) ??
        calculateRentFree(bookingForm?.leaseTerms?.rentFreeStartDate, bookingForm?.leaseTerms?.rentFreeEndDate) ??
        rentFreePeriodOptions[0],
      commencementDate: initialCommencementDate,
      moveInDate: initialMoveInDate,
      noOfFreeParkingSlots: bookingForm?.leaseTerms?.noOfFreeParkingSlots,
      parkings: bookingForm?.parkings || [],
    } satisfies LeaseTermFormData,
  });

  const {
    control,
    clearErrors,
    setValue,
    setError,
    setFocus,
    getValues,
    watch,
    handleSubmit,
    formState: { errors },
  } = methods;

  const parkingsWatcher = useWatch({ control, name: "parkings" });
  const watchedFormValues = watch();

  const isBooking = Object.keys(bookingDetails).length > 0;

  useEffect(() => {
    const subscription = watch((_, { name }) => {
      if (name) clearErrors(name as keyof LeaseTermFormData);
    });
    return () => subscription.unsubscribe();
  }, [watch, clearErrors]);

  useEffect(() => {
    const userDetails = JSON.parse(localStorage.getItem("login-response"));

    if (!isBooking || !bookingDetails?.leaseTerms) {
      const currentDate = dayjs();

      const leaseStartDate = currentDate.add(14, "day");

      const data = {
        leaseManager: userDetails.userId,
        leaseTerms: {
          // moveInDate: leaseStartDate,
          leaseStartDate: leaseStartDate,
          leaseNature: bookingForm?.leaseTerms?.leaseNature,
          leaseEndDate: leaseStartDate.subtract(1, "day").add(1, "year"),
        },
        bookingTerms: {
          bookingStartDate: currentDate,
          bookingEndDate: currentDate.add(14, "day"),
          ...bookingForm?.bookingTerms, //ADDED FIX FOR BUG: 7756
        },
        parkings: bookingForm?.parkings,
      };

      if (Object.keys(bookingForm?.leaseTerms || {}).length > 0)
        data.leaseTerms = { ...bookingForm?.leaseTerms, ...data.leaseTerms };
      dispatch(setBookingForm(data));
    }
  }, []);

  const onSubmit = async (data: any, type) => {
    let dataCopy = { ...(data || getValues()) };
    let body = {
      externalAgent: dataCopy?.externalAgent,
      leaseManager: dataCopy.leaseManager?.value,
      numberOfOcupants: dataCopy.numberOfOcupants,
      leaseTerms: {
        leaseType: "new",
        leaseNature: dataCopy.leaseNature?.value,
        moveInDate: formatDateByType(dataCopy.moveInDate, "api"),
        leaseEndDate: formatDateByType(dataCopy.leaseEndDate, "api"),
        leaseStartDate: formatDateByType(dataCopy.leaseStartDate, "api"),
        numberOfYears: dataCopy?.numberOfYears?.value,
        noOfFreeParkingSlots: dataCopy?.noOfFreeParkingSlots === "" ? 0 : Number(dataCopy?.noOfFreeParkingSlots),
        rentFreePeriod: dataCopy?.rentFreePeriod?.value,
        commencementDate: formatDateByType(dataCopy?.commencementDate, "api"),
        ...(parseInt(dataCopy?.rentFreePeriod?.value) > 0
          ? {
              rentFreeStartDate: formatDateByType(leaseEndDateRef.current.actual, "api"),
              rentFreeEndDate: formatDateByType(leaseEndDateRef.current.withRentFreePeriod, "api"),
            }
          : {}),
      },
      bookingTerms: {
        ...bookingForm?.bookingTerms,
        bookingEndDate: formatDateByType(dataCopy.bookingEndDate, "api"),
        bookingStartDate: formatDateByType(dataCopy.bookingStartDate, "api"),
      },
      parkings: parkingPayload(dataCopy?.parkings),
    };

    if (type === "next") {
      let valid = true;

      Object.keys(dataCopy).forEach((item) => {
        const key = item as keyof LeaseTermFormData;
        if (!excludedLeaseTermsKeys.includes(key)) {
          const value = dataCopy[key];
          if (value) return;

          setError(key, { type: "manual", message: `Please enter ${key.replace(/([A-Z])/g, " $1").toLowerCase()}` });
          setFocus(key);
          valid = false;
        }
      });

      if (!valid) return;

      dispatch(setFormState());
      dispatch(setBookingForm(body));
    }
    body = { ...bookingForm, ...body };

    props.parentState({ load: true });

    try {
      const response = await dispatch(updateBooking({ leaseId, body }));

      await dispatch(getBookingDetail(leaseId));

      message.success(response.data.message);
    } catch (error) {
      message.error(error.response?.data?.error || error.response?.data?.message);
    } finally {
      props.parentState({ load: false });
    }
  };

  return (
    <>
      <CommonCard
        label="Lease Terms"
        leftBtnLabel="Back"
        rightBtnLabel="Next"
        handleNext={onSubmit}
        handleSaveDraft={methods.handleSubmit(onSubmit)}
        parentState={props.parentState}
        handleBack={() => dispatch(setFormState("back"))}
        handeLeaseConfig={() => {
          setTrigger(0);
          setVisible(!visible);
        }}
      >
        <ResidentLeaseTermsForm
          leaseEndDateRef={leaseEndDateRef}
          setValue={setValue}
          watchedFormValues={watchedFormValues}
          control={control}
          getValues={getValues}
          errors={errors && errors}
          setError={setError}
          methods={methods}
          onSubmit={onSubmit}
          handleSubmit={handleSubmit}
          parkingsWatcher={parkingsWatcher}
        />
      </CommonCard>
      <PopupManager trigger={trigger} isOpen={visible} onClose={() => setVisible(false)} />
    </>
  );
};

export default index;
